import { faTimesCircle } from '@fortawesome/sharp-solid-svg-icons';
import { useRollbar } from '@rollbar/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { CompanyType, OfferType, OrderType } from 'src/types';

import { zodResolver } from '@hookform/resolvers/zod';
import { Turnstile } from '@marsidev/react-turnstile';
import { AddressForm } from 'src/components/Checkout/AddressForm';
import { PersonalDataForm } from 'src/components/Checkout/PersonalDataForm';
import { CheckboxField } from 'src/components/Form/CheckboxField';
import { Button } from 'src/components/ui/Button';
import { Icon } from 'src/components/ui/Icon';
import { Text } from 'src/components/ui/Text';
import { Title } from 'src/components/ui/Title';
import { useRequest } from 'src/hooks/useRequest';
import { useTracking } from 'src/providers/TrackingProvider';
import { CheckoutSchema, CheckoutSchemaFields } from 'src/schemas/CheckoutSchema';
import { CheckoutSerializer } from 'src/serializers/CheckoutSerializer';
import { createOrder, updateOrder } from 'src/services/PlatformAPI';
import { CheckoutSteps } from 'src/utils/constants/steps';
import { CheckoutTypeEnum } from 'src/utils/constants/types';

type CheckoutOrderFormProps = {
  company: CompanyType;
  offer: OfferType;
  contractId: string;
  order?: OrderType;
  onOrderChange?: (order: OrderType) => void;
  onStepChange?: (step: CheckoutSteps) => void;
};

const CheckoutOrderForm = (props: CheckoutOrderFormProps) => {
  const { offer, contractId } = props;
  const rollbar = useRollbar();
  const { addParams, logEvent } = useTracking();
  const router = useRouter();
  const creatingOrder = !props.order || !props.order.id;
  const checkoutType = creatingOrder ? offer.checkoutType : props.order.checkoutType;
  const isAddressRequired = checkoutType !== CheckoutTypeEnum.WITHOUT_ADDRESS;
  const shouldLoadCaptchaOnInit = Boolean(props.order) || !isAddressRequired;
  const [captchaToken, setCaptchaToken] = useState('');
  const [loadCaptcha, setLoadCaptcha] = useState(shouldLoadCaptchaOnInit);
  const captchaRef = useRef(null);

  const methods = useForm<CheckoutSchemaFields>({
    mode: 'onChange',
    resolver: zodResolver(CheckoutSchema({ isAddressRequired })),
    defaultValues: CheckoutSerializer.serializeToForm(props.order),
  });

  const {
    getValues,
    formState: { errors },
  } = methods;

  const orderMutation = useRequest<OrderType>(creatingOrder ? createOrder : updateOrder, {
    onSuccess: (data: OrderType) => {
      if (creatingOrder) {
        window.fbq && window.fbq('track', 'Lead');
        router.push(`/${props.company.slug}/pedido?order_id=${data.id}`);
        return;
      }

      props.onOrderChange(data);
      props.onStepChange(CheckoutSteps.PAYMENT);
    },
    onError: (error) => {
      const response = error?.response;

      setCaptchaToken('');
      captchaRef.current?.reset();

      if (response?.status === 422 && response?.data?.error === 'invalid_captcha_token') return;

      if (response?.status === 422 && response?.data?.error === 'record_invalid') {
        const responseErrors = response.data.messages.reduce(
          (previous, current) => {
            const handledError = API_RESPONSE_TO_FORM_FIELDS[current.attribute];
            if (!handledError) {
              return {
                ...previous,
                unhandledErrors: [...previous.unhandledErrors, { [current.attribute]: current.message }],
              };
            }

            return {
              ...previous,
              handledErrors: [...previous.handledErrors, handledError],
            };
          },
          { handledErrors: [], unhandledErrors: [] },
        );

        responseErrors.handledErrors.forEach((error) => {
          methods.setError(error.attribute, { message: error.message, type: 'custom' });
        });

        logEvent({
          event: 'order_form_record_invalid',
          eventProperties: response.data.messages.map((message) => message.attribute),
        });

        if (responseErrors.unhandledErrors.length) {
          rollbar.error('Error creating order', { data: responseErrors.unhandledErrors });
        }
      }
    },
  });

  const executeOrderRequest = () =>
    orderMutation.executeRequest(
      CheckoutSerializer.serialize({
        ...getValues(),
        contractId,
        queryParams: router.query,
        offerId: offer.id,
        orderId: props.order?.id,
        captchaToken,
      }),
    );

  const onSubmit = async (data: CheckoutSchemaFields) => {
    const event = creatingOrder ? 'clicked_createorder' : 'clicked_updateorder';

    addParams({ customerEmail: data.email });
    logEvent({
      event,
      eventProperties: {
        offerId: props.offer.id,
        orderId: props.order?.id,
      },
    });

    executeOrderRequest();
  };

  const onCaptchaVerify = (captchaResponse) => {
    setCaptchaToken(captchaResponse);
  };

  useEffect(() => {
    const errorLabel = document.querySelectorAll('.js-form-control-invalid-error')[0];

    if (errorLabel) {
      errorLabel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errors]);

  useEffect(() => {
    const errorLabel = document.querySelector('.js-order-error');

    if (errorLabel) {
      errorLabel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [orderMutation.isError]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {orderMutation.isError && (
          <div className="js-order-error mb-8 flex gap-4 rounded border border-negative-most p-4">
            <Icon
              icon={faTimesCircle}
              size="md"
              className="text-negative-most"
            />

            <div className="flex flex-col gap-1">
              <Title
                size="sm"
                className="text-negative-most"
              >
                Não poderemos continuar
              </Title>
              <Text className="text-negative-most">
                Não foi possível continuar com seu pedido, tente novamente mais tarde.
              </Text>
            </div>
          </div>
        )}

        <Title size="sm">Dados pessoais</Title>

        <PersonalDataForm />

        {isAddressRequired && (
          <>
            <div className="my-8 h-px bg-background-least" />

            <Title size="sm">Endereço</Title>

            <AddressForm
              order={props.order}
              onAddressLoad={() => setLoadCaptcha(true)}
            />
          </>
        )}

        <CheckboxField
          className="mt-10"
          name="accept"
          label={
            <span data-testid="accept-terms">
              Li, entendi e estou de acordo com os{' '}
              <Link
                href="/termos-de-uso"
                target="_blank"
                className="font-medium underline"
                onClick={() => logEvent({ event: 'clicked_readuseterms' })}
              >
                Termos de Uso
              </Link>{' '}
              da Lia.
            </span>
          }
        />

        <div className="mt-10 flex flex-wrap items-center justify-end gap-4">
          <div className="flex-1">
            {loadCaptcha && (
              <Turnstile
                ref={captchaRef}
                siteKey={process.env.NEXT_PUBLIC_TURNSTILE_CAPTCHA_KEY}
                onSuccess={onCaptchaVerify}
                onError={() => setCaptchaToken('')}
                onExpire={() => setCaptchaToken('')}
                onTimeout={() => setCaptchaToken('')}
                options={{
                  action: 'create_order',
                  language: 'pt-br',
                }}
              />
            )}
          </div>

          <Button
            variant="solid"
            size="lg"
            type="submit"
            className="w-full sm:w-auto"
            isLoading={orderMutation.isLoading || orderMutation.isSuccess}
            disabled={!captchaToken}
          >
            Continuar
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

const API_RESPONSE_TO_FORM_FIELDS = { address: { attribute: 'phone', message: 'O celular está inválido' } };

export { CheckoutOrderForm };
